<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-5xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="sci-page-header pb-4">
        <div>
          <h1 class="sci-page-title-only">Clientes Cartão Black</h1>
          <p class="text-sm text-slate-500">Gerencie as pessoas autorizadas a utilizar os cartões premium</p>
        </div>
      </div>

      <!-- ÁREA DE PESQUISA -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm mb-8">
          <div class="px-6 py-4" style="background-color: #333333;">
              <h2 class="text-white font-bold flex items-center gap-2" style="font-size: 11px; letter-spacing: 0.1em;">
                  <v-icon color="white" small class="mr-2">mdi-magnify</v-icon>
                  PESQUISAR
              </h2>
          </div>
          <div class="px-6 pt-6 pb-10">
              <div class="flex items-end gap-4">
                  <div class="flex-1 w-full">
                      <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Prefeitura</label>
                      <div class="relative">
                          <v-autocomplete
                            v-model="filterPrefecture"
                            :items="prefectures"
                            item-text="prefecture_social_name"
                            item-value="prefecture_id"
                            placeholder="Selecione uma opção..."
                            solo
                            flat
                            dense
                            hide-details
                            clearable
                            class="custom-select"
                            height="48"
                          >
                             <template v-slot:item="{ item }">
                                <span class="text-[10px] font-bold uppercase tracking-widest text-slate-700">{{ item.prefecture_social_name }}</span>
                             </template>
                          </v-autocomplete>
                      </div>
                  </div>
                  <div class="flex-none">
                      <button @click="fetchClients" class="bg-orange-500 hover:bg-orange-600 text-white rounded-lg shadow-md shadow-orange-500/10 flex items-center justify-center gap-2 w-fit whitespace-nowrap px-8 h-12 transition-all transform hover:-translate-y-0.5">
                          <v-icon color="white" small class="mr-1" style="font-size: 14px;">mdi-filter-outline</v-icon>
                          <span class="text-[11px] font-black uppercase">FILTRAR</span>
                      </button>
                  </div>
              </div>
          </div>
      </section>

      <!-- GRID DE DADOS -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center justify-between" style="background-color: #313131;">
              <div class="flex items-center gap-3">
                <v-icon color="orange" small>mdi-account-group-outline</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">CLIENTES REGISTRADOS</h2>
              </div>
              
              <div class="flex items-center gap-4 flex-1 justify-end">
                <div class="flex-none">
                    <button 
                      @click="$router.push('/settings/black-cards/clients/create')" 
                      class="bg-orange-500 hover:bg-orange-600 text-white shadow-lg flex items-center justify-center gap-2 rounded-lg px-6 py-2 transition-all w-fit max-w-max whitespace-nowrap"
                    >
                      <v-icon color="white" small>mdi-plus</v-icon>
                      <span class="font-bold text-sm tracking-wider uppercase">NOVO</span>
                    </button>
                </div>

                <div class="relative w-80">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <v-icon color="white" small class="opacity-50">mdi-magnify</v-icon>
                  </div>
                  <input 
                    v-model="search"
                    placeholder="PESQUISAR CLIENTE OU DOCUMENTO..."
                    class="bg-white/10 border border-white/20 rounded-lg py-2 pl-10 pr-4 text-[10px] text-white placeholder:text-white/40 focus:outline-none focus:bg-white/20 transition-all w-full uppercase tracking-widest h-[40px]"
                  >
                </div>
              </div>
          </div>

          <div class="p-0">
              <v-data-table
                :headers="headers"
                :items="filteredClients"
                :search="search"
                :items-per-page="10"
                :loading="loader"
                loading-text="CARREGANDO CLIENTES..."
                class="custom-table"
                hide-default-footer
              >
                <template v-slot:item.client_name="{ item }">
                  <div class="flex flex-col py-2">
                    <span class="text-sm font-bold text-slate-700 uppercase">{{ item.client_name }}</span>
                    <span class="text-[10px] text-slate-400 font-medium tracking-wider">{{ item.client_document || 'SEM DOCUMENTO' }}</span>
                  </div>
                </template>

                <template v-slot:item.prefecture_social_name="{ item }">
                   <v-chip x-small label dark color="#444" class="font-bold border border-white/10">{{ item.prefecture_social_name }}</v-chip>
                </template>

                <template v-slot:item.action="{ item }">
                  <div class="flex justify-end gap-2 px-2">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <button v-bind="attrs" v-on="on" @click="$router.push(`/settings/black-cards/clients/edit/${item.client_id}`)" class="p-2 hover:bg-slate-100 text-slate-500 rounded-lg transition-colors">
                          <v-icon small>mdi-account-edit</v-icon>
                        </button>
                      </template>
                      <span>Editar</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <button v-bind="attrs" v-on="on" @click="deleteItem(item.client_id)" class="p-2 hover:bg-red-50 text-red-400 rounded-lg transition-colors">
                          <v-icon small>mdi-trash-can-outline</v-icon>
                        </button>
                      </template>
                      <span>Excluir</span>
                    </v-tooltip>
                  </div>
                </template>
              </v-data-table>
          </div>
      </section>
    </div>

    <!-- Feedback -->
    <v-snackbar v-model="snack.show" :color="snack.color" timeout="3000" top right>
      <div class="flex items-center gap-3">
        <v-icon color="white" small>{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        <span class="text-[11px] font-bold uppercase tracking-wider">{{ snack.message }}</span>
      </div>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  data: () => ({
    search: "",
    filterPrefecture: null,
    loader: false,
    clients: [],
    prefectures: [],
    snack: {
      show: false,
      message: "",
      color: "success"
    },
    headers: [
      { text: "Cliente", align: "start", value: "client_name", sortable: true },
      { text: "Prefeitura", align: "start", value: "prefecture_social_name", sortable: true },
      { text: "Ações", align: "right", value: "action", sortable: false }
    ]
  }),
  created() {
    this.fetchPrefectures();
    this.fetchClients();
  },
  computed: {
    filteredClients() {
      if (!this.filterPrefecture) return this.clients;
      return this.clients.filter(c => c.prefecture_id === this.filterPrefecture);
    }
  },
  methods: {
    fetchPrefectures() {
      this.$http.get("/prefectures").then(r => {
        this.prefectures = r.data;
      });
    },
    fetchClients() {
      this.loader = true;
      this.$http.get("/settings/black-cards/clients").then(r => {
        this.clients = r.data;
        this.loader = false;
      }).catch(e => {
        this.loader = false;
        console.error(e);
      });
    },
    deleteItem(id) {
      if (confirm("Deseja realmente excluir este cliente? Isso pode afetar os cartões vinculados.")) {
        this.$http.delete(`/settings/black-cards/clients/${id}`).then(r => {
          this.snack.show = true;
          this.snack.message = r.data.result;
          this.snack.color = "success";
          this.fetchClients();
        });
      }
    }
  }
};
</script>

<style scoped>
.custom-input >>> .v-input__slot {
  background-color: #f8fafc !important;
  border-radius: 8px !important;
}
.custom-select >>> .v-input__slot {
  background-color: #f8fafc !important;
  border-radius: 8px !important;
  padding: 0 16px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
}
.custom-select >>> .v-select__selection {
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  color: #334155 !important;
}
.custom-select >>> .v-label {
  font-size: 11px !important;
  font-weight: 500 !important;
  color: #94a3b8 !important;
}
.custom-table >>> th {
  background-color: #f8fafc !important;
  font-size: 10px !important;
  font-weight: 700 !important;
  color: #64748b !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  padding: 16px !important;
}
.custom-table >>> td {
  padding: 12px 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}
</style>
